import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import React, { forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';

export interface SearchBarProps {
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  className?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  disabled?: boolean;
  isLoading?: boolean;
}

const sizeClasses = {
  xs: 'px-10 py-1 text-xs',
  sm: 'px-10 py-2 text-sm',
  md: 'px-10 py-4 text-base',
  lg: 'px-10 py-6 text-base',
  xl: 'px-10 py-8 text-lg',
};

export const SearchBar = forwardRef<HTMLInputElement, SearchBarProps>(
  (
    {
      placeholder = 'Search',
      value = '',
      onChange,
      onKeyDown,
      className,
      size = 'md',
      disabled = false,
      isLoading = false,
    },
    ref
  ) => {
    const handleClear = () => {
      onChange?.('');
    };

    return (
      <div className={twMerge('relative w-full', className)}>
        <div className="absolute ml-3 flex h-full items-center justify-center">
          {isLoading ? (
            <svg
              className="text-chariot-text-secondary size-5 animate-spin"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              aria-hidden="true"
              data-testid="search-bar-spinner"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          ) : (
            <MagnifyingGlassIcon
              className="text-chariot-text-secondary size-5"
              data-testid="search-bar-icon"
            />
          )}
        </div>
        <input
          type="text"
          ref={ref}
          value={value}
          onChange={e => onChange?.(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          role="searchbox"
          aria-label={placeholder}
          aria-autocomplete="list"
          className={twMerge(
            'bg-chariot-transparent text-chariot-text-primary w-full rounded-md p-3 pr-10 pl-10 text-base',
            'border-0',
            'placeholder:text-chariot-text-secondary',
            disabled && 'cursor-not-allowed opacity-75',
            sizeClasses[size]
          )}
          data-testid="search-bar"
        />
        {value && (
          <button
            onClick={handleClear}
            className="absolute top-0 right-3 flex h-full items-center justify-center"
            aria-label="Clear search"
          >
            <XMarkIcon className="text-chariot-text-primary hover:text-chariot-text-primary size-5 transition-colors" />
          </button>
        )}
      </div>
    );
  }
);

SearchBar.displayName = 'SearchBar';
